import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { faqHostApi } from '@/api/services/faqHost';
import type {
  CreateFaqDto,
  CreateUpdateFaqCategoryDto,
  UpdateFaqDto,
} from '@/api/types/faqHost';

export const faqHostKeys = {
  all: ['faqHost'] as const,
  list: ['faqHost', 'list'] as const,
  categoryFeatures: (id: string) => ['faqHost', 'categoryFeatures', id] as const,
};

export function useFaqList() {
  return useQuery({
    queryKey: faqHostKeys.list,
    queryFn: () => faqHostApi.getListWithFaqs(),
  });
}

export function useCategoryFeatureNames(categoryId: string | null) {
  return useQuery({
    queryKey: faqHostKeys.categoryFeatures(categoryId ?? ''),
    queryFn: () => faqHostApi.getCategoryFeatureNames(categoryId as string),
    enabled: categoryId !== null,
  });
}

function useInvalidateList() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: faqHostKeys.list });
}

export function useCreateCategory() {
  const invalidate = useInvalidateList();
  return useMutation({
    mutationFn: (input: CreateUpdateFaqCategoryDto) => faqHostApi.createCategory(input),
    onSuccess: invalidate,
  });
}

export function useUpdateCategory() {
  const invalidate = useInvalidateList();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CreateUpdateFaqCategoryDto }) =>
      faqHostApi.updateCategory(id, input),
    onSuccess: invalidate,
  });
}

export function useDeleteCategory() {
  const invalidate = useInvalidateList();
  return useMutation({
    mutationFn: (id: string) => faqHostApi.deleteCategory(id),
    onSuccess: invalidate,
  });
}

export function useUpdateCategoryFeatures() {
  const invalidate = useInvalidateList();
  return useMutation({
    mutationFn: ({ categoryId, features }: { categoryId: string; features: string[] }) =>
      faqHostApi.updateCategoryFeatures(categoryId, features),
    onSuccess: invalidate,
  });
}

export function useCreateFaq() {
  const invalidate = useInvalidateList();
  return useMutation({
    mutationFn: (input: CreateFaqDto) => faqHostApi.createFaq(input),
    onSuccess: invalidate,
  });
}

export function useUpdateFaq() {
  const invalidate = useInvalidateList();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateFaqDto }) =>
      faqHostApi.updateFaq(id, input),
    onSuccess: invalidate,
  });
}

export function useDeleteFaq() {
  const invalidate = useInvalidateList();
  return useMutation({
    mutationFn: (id: string) => faqHostApi.deleteFaq(id),
    onSuccess: invalidate,
  });
}
